import { Button } from "../Button";
import { FileUpload } from "../inputs";
import { useState } from "react";
import localization from "../../../config/localization";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface UploadDocumentFormData {
  userRemarks: string;
  file: File | null;
}

interface UploadDocumentModalProps {
  isOpen: boolean;
  title: string;
  titleClassName?: string;
  warningText?: string;
  warningTextClassName?: string;
  blurColor?: string;
  submitText?: string;
  onSubmit: (data: UploadDocumentFormData) => void;
  onCancel: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png"];

export function UploadDocumentModal({
  isOpen,
  title,
  titleClassName,
  warningText,
  warningTextClassName,
  blurColor = "bg-[#FFE6CC]",
  submitText = "Submit Request",
  onSubmit,
  onCancel,
}: UploadDocumentModalProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UploadDocumentFormData>({
    userRemarks: "",
    file: null,
  });

  const validateImage = (file: File): string | null => {
    const extension = "." + file.name.split(".").pop()?.toLowerCase();

    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return localization.img_extention;
    }

    if (file.size > MAX_FILE_SIZE) {
      return localization.img_size;
    }
    return null;
  };

  const handleFileSelect = (file: File | null) => {
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setError(err);
      setFormData((prev) => ({
        ...prev,
        file: null,
      }));
      setPreview(null);
      return;
    }
    setError(null);
    setFormData((prev) => ({
      ...prev,
      file,
    }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    const data: UploadDocumentFormData = {
      userRemarks: formData.userRemarks.trim(),
      file: formData.file,
    };

    onSubmit(data);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[9999] bg-black/70`}>
      <div
        className={`fixed top-1/2 left-1/2 px-4
                      -translate-x-1/2 -translate-y-1/2 w-full max-w-[480px] max-h-[90vh]`}
      >
        <div className="relative rounded-2xl bg-white p-6 text-center shadow-xl">
          <Button
            onClick={onCancel}
            className="absolute top-4 right-4 h-6 w-6 rounded-full bg-[#F2F2F2] text-[#838383]"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
          <div className="overflow-y-auto max-h-[60vh] scrollbar-hide">
            <div className="relative mx-auto h-[125px] w-[125px] flex items-center justify-center">
              <div
                className={`absolute inset-0 rounded-full blur-[25px] ${
                  blurColor
                }`}
              />
              <div className="relative z-10 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-white">
                <img
                  src={"/icons/ic-info-blue.svg"}
                  alt="Info"
                  className="h-[40px] w-[40px]"
                />
              </div>
            </div>
            <div className="row gap-2">
              <h2
                className={`mt-2 text-lg font-semibold text-black ${titleClassName}`}
              >
                {title}
              </h2>

              {warningText && (
                <p
                  className={`mt-2 text-sm font-medium text-[#A51C1B] ${warningTextClassName}`}
                >
                  {warningText}
                </p>
              )}

              <div className="mt-4">
                <div>
                  <label className="flex font-semibold text-sm text-[#2F2F2F] my-2">
                    {localization.write_your_additiona_concerns}
                  </label>
                  <textarea
                    className="w-full font-semibold text-sm text-[#3C3C3C] bg-[#F6F6F6] border border-[#E8E8E8] rounded-[4px] p-2 h-[75px] placeholder-[#3C3C3C]"
                    placeholder={localization.enter_additional_concerns}
                    value={formData.userRemarks}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        userRemarks: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="mt-2">
                  <FileUpload
                    label={localization.upload_document_lbl}
                    labelClassName="flex font-semibold text-[#2F2F2F]"
                    placeholder={localization.upload_file_document}
                    helperText={`${localization.upload_image_lbl} ${localization.upload_image_lbl_size}`}
                    previewHeight={150}
                    borderClassName="border-2 border-dashed border-[#BDBDBD]"
                    canPreview={true}
                    preview={preview}
                    blackTheme={false}
                    error={error ?? undefined}
                    onSelect={handleFileSelect}
                    onRemove={() => {
                      setFormData((prev) => ({
                        ...prev,
                        file: null,
                      }));
                      setPreview(null);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-4">
            <Button
              onClick={handleSubmit}
              className="w-full rounded-lg bg-black py-3 font-medium text-white"
            >
              {submitText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
